import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BadgeColor } from '../badge-single-color/model';
import {
  BADGE_COLOR_MAP,
  BADGE_WIDGET_FALLBACK_PAIR,
  resolveBadgeColorValue,
} from '../../helpers/widget-color.helper';

@Component({
  selector: 'app-progress-bar-widget',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './progress-bar-widget.component.html',
  styleUrl: './progress-bar-widget.component.scss',
})
export class ProgressBarWidgetComponent implements OnChanges {
  private _value: number = 0;
  private _max: number = 100;

  @Input()
  set value(val: number) {
    this._value = val;
    this.calculatePercentage();
  }
  get value(): number {
    return this._value;
  }

  @Input()
  set max(val: number) {
    this._max = val;
    this.calculatePercentage();
  }
  get max(): number {
    return this._max;
  }

  @Input() tooltip?:
    | string
    | ((value: number, max: number, percentage: number) => string);
  @Input() evaluator?: (percentage: number) => BadgeColor | string;
  @Input() defaultColor?: BadgeColor;
  @Input() customColor?: string;
  @Input() width: string = '100%';
  /** When set, replaces the default percentage label on the bar. */
  @Input() label?: string;
  @Input() action?: (value: any) => void;
  @Input() translationPrefix?: string;

  percentage: number = 0;
  barColor: string = '';

  ngOnInit(): void {
    this.calculatePercentage();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.calculatePercentage();
  }

  private calculatePercentage(): void {
    this.percentage = Math.min(
      Math.max((this._value / this._max) * 100, 0),
      100,
    );
    this.updateBarColor();
  }

  private updateBarColor(): void {
    if (this.customColor) {
      this.barColor = this.customColor;
      return;
    }

    if (this.evaluator) {
      const evaluatedColor = this.evaluator(this.percentage)?.trim();
      if (evaluatedColor) {
        this.barColor = resolveBadgeColorValue(evaluatedColor);
        return;
      }
    }

    if (this.defaultColor) {
      this.barColor = BADGE_COLOR_MAP[this.defaultColor];
      return;
    }

    this.barColor = BADGE_WIDGET_FALLBACK_PAIR.bg;
  }

  getTooltip(): string {
    if (typeof this.tooltip === 'function') {
      return this.tooltip(this._value, this._max, this.percentage);
    }

    return this.tooltip ?? '';
  }

  isTooltipEnabled(): boolean {
    return this.tooltip !== undefined && this.tooltip !== null;
  }

  onClick(event: MouseEvent): void {
    if (this.action) {
      event.preventDefault();
      event.stopPropagation();
      this.action(this.value);
    }
  }
}
